import React, { useState, useEffect } from 'react';
import { LogoIcon } from './Icons';
import EmailCapture from './EmailCapture';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "NEW NAME, SAME BILLY",
      subtitle: "Scarborough's legendary wings are back with the same unbeatable flavor, now under the bright neon glow of Billy's WingCo.",
      ctaText: "View Menu",
      ctaLink: "#menu",
      secondaryCTA: "Order Now",
      secondaryLink: "#order"
    },
    {
      id: 2,
      title: "LEGENDARY WINGS",
      subtitle: "Same legendary flavors you love, now with craft cocktails and an electric atmosphere that'll keep you coming back for more.",
      ctaText: "See Wings",
      ctaLink: "#menu",
      secondaryCTA: "Order Now",
      secondaryLink: "#order"
    },
    {
      id: 3,
      title: "CRAFT COCKTAILS",
      subtitle: "Elevate your wing night with our signature drinks, from Billy's Marg to Sweet Heat Sipper - the perfect complement to our bold flavors.",
      ctaText: "See Drinks",
      ctaLink: "#menu",
      secondaryCTA: "Order Now",
      secondaryLink: "#order"
    }
  ];

  // Auto-advance slides every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section id="home" className="min-h-screen flex flex-col items-center justify-center text-center relative overflow-hidden
                                   px-3 sm:px-4 pt-16 sm:pt-20 md:pt-24
                                   bg-gradient-to-b from-black via-black/95 to-black">
      {/* Background overlay for better text readability */}
      <div className="absolute inset-0 bg-black/30 z-0"></div>
      
      <div className="relative z-10 w-full max-w-6xl mx-auto h-full min-h-[calc(100vh-4rem)] sm:min-h-[calc(100vh-5rem)]
                      flex flex-col justify-center items-center py-8 sm:py-12">
        
        {/* Logo Section - Perfectly centered with equal spacing */}
        <div className="flex-1 flex items-center justify-center py-6 sm:py-8">
          <LogoIcon className="w-64 sm:w-80 md:w-[28rem] lg:w-[32rem] animate-flicker mx-auto max-w-[90vw] h-auto" />
        </div>
        
        {/* Content Container - Equal spacing with logo */}
        <div className="flex-1 flex flex-col justify-center py-6 sm:py-8 space-y-6 sm:space-y-8">
          {/* Carousel Content */}
          <div className="relative w-full overflow-hidden">
            <div className="flex transition-transform duration-500 ease-in-out w-full min-h-[200px] sm:min-h-[250px] md:min-h-[280px]"
                 style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {slides.map((slide, index) => (
                <div key={slide.id} className="w-full flex-shrink-0 px-2 sm:px-4 flex flex-col justify-center">
                  <div className="text-center space-y-3 sm:space-y-4 md:space-y-6">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-logoSans tracking-wide text-ivory 
                                   max-w-4xl mx-auto leading-tight px-2">
                      {slide.title}
                    </h1>
                    
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray max-w-xl sm:max-w-2xl 
                                  leading-relaxed px-2 mx-auto">
                      {slide.subtitle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Email Capture */}
          <div className="w-full max-w-md mx-auto px-2">
            <EmailCapture className="w-full" />
          </div>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-md mx-auto px-2 cta-buttons">
            <a
              href={slides[currentSlide].ctaLink}
              className="bg-neonBlue text-black px-4 sm:px-6 py-3 sm:py-3 rounded-lg font-semibold
                         shadow-neonBlue hover:animate-flicker hover:-translate-y-0.5
                         transition-all duration-200 min-h-[48px] inline-flex items-center justify-center
                         text-sm sm:text-base touch-target"
            >
              {slides[currentSlide].ctaText}
            </a>
            
            <a
              href={slides[currentSlide].secondaryLink}
              className="border-2 border-neonRed text-neonRed px-4 sm:px-6 py-3 sm:py-3 rounded-lg font-semibold
                         hover:bg-neonRed hover:text-black hover:shadow-neonRed 
                         hover:-translate-y-0.5 transition-all duration-200 min-h-[48px]
                         inline-flex items-center justify-center text-sm sm:text-base touch-target"
            >
              {slides[currentSlide].secondaryCTA}
            </a>
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-3 sm:gap-4">
            {/* Previous Button */}
            <button
              onClick={prevSlide}
              className="text-gray hover:text-neonBlue transition-colors duration-200 p-2 sm:p-3 touch-target 
                         rounded-lg hover:bg-black/30 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Previous slide"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Slide Indicators */}
            <div className="flex gap-2 sm:gap-3">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-200 touch-target 
                             min-w-[44px] min-h-[44px] flex items-center justify-center relative ${
                    index === currentSlide
                      ? 'bg-neonBlue shadow-neonBlue'
                      : 'bg-gray/30 hover:bg-gray/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${
                    index === currentSlide ? 'bg-neonBlue' : 'bg-gray/30'
                  }`}></span>
                </button>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={nextSlide}
              className="text-gray hover:text-neonBlue transition-colors duration-200 p-2 sm:p-3 touch-target 
                         rounded-lg hover:bg-black/30 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Next slide"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 